import type { Vec3 } from "./utils.js";
import {
  SUPPORT_AURA_RANGE,
  GUARDIAN_TAUNT_RADIUS,
  CHOREO_AOE_RADIUS,
  RADAR_RING_RADIUS,
  CAGE_SURROUND_RADIUS,
  ORBIT_ANIM_RADIUS,
} from "./constants.js";

// ── Enums ────────────────────────────────────────────────────────────────────

export enum DroneType {
  Striker = 0,
  Sniper = 1,
  Healer = 2,
  Interceptor = 3,
  Support = 4,
  Guardian = 5,
}

export enum Tier {
  Common = 0,
  Rare = 1,
  Legend = 2,
}

export enum AlgorithmCardType {
  Regroup = 0,
  Scatter = 1,
  Follow = 2,
  Orbit = 3,
  DefensiveWall = 4,
  RadarRing = 5,
  FocusFire = 6,
  RelayNet = 7,
  CageSurround = 8,
  SyncDive = 9,
  ConvertShot = 10,
  ChoreoUltimate = 11,
  HorizontalOrbit = 12,
  DiagonalOrbit = 13,
}

export enum PickupType {
  Drone = 0,
  Algorithm = 1,
}

export enum CommandType {
  Move = "move",
  ActivateCard = "activate_card",
  DiscardCard = "discard_card",
  SetTarget = "set_target",
  CancelOrder = "cancel_order",
  SelectDrone = "select_drone",
  SetLeader = "set_leader",
  TriggerAbility = "trigger_ability",
  FireBasic = "fire_basic",
  DismissDrone = "dismiss_drone",
  // === U7: admin debug commands === BEGIN (appended — never reorder)
  AdminSpawnDummy = "admin_spawn_dummy",
  AdminDamageSelf = "admin_damage_self",
  AdminResetArena = "admin_reset_arena",
  // === U7: admin debug commands === END
}

export enum ServerEvent {
  DroneDestroyed = "drone_destroyed",
  PlayerKilled = "player_killed",
  PickupCollected = "pickup_collected",
  CardActivated = "card_activated",
  PlayerRespawned = "player_respawned",
  DamageDealt = "damage_dealt",
  HealApplied = "heal_applied",
  AbilityTriggered = "ability_triggered",
  // ── Battle Royale events (appended — never reorder) ───────────────────────
  PlayerEliminated = "player_eliminated",
  ZoneShrinking = "zone_shrinking",
  MatchStarting = "match_starting",
  MatchWon = "match_won",
  ProjectileFired = "projectile_fired",
  // === Unit 6: rematch === BEGIN
  RematchVoteUpdate = "rematch_vote_update",
  RematchReady = "rematch_ready",
  RematchFailed = "rematch_failed",
  // === Unit 6: rematch === END
  // === U5: kill-streak announcer === BEGIN
  KillStreak = "kill_streak",
  // === U5: kill-streak announcer === END
  // === Unit 3: shield-break feedback === BEGIN (appended — never reorder)
  ShieldBroken = "shield_broken",
  // === Unit 3: shield-break feedback === END
  // === Unit 4: ConvertShot glitch === BEGIN
  DroneConverted = "drone_converted",
  // === Unit 4: ConvertShot glitch === END
  // === U4: stun status === BEGIN (appended — never reorder)
  Stunned = "stunned",
  // === U4: stun status === END
  // === U8: assist tracking === BEGIN
  AssistGained = "assist_gained",
  // === U8: assist tracking === END
  // === U3: damage-over-time === BEGIN (appended — never reorder)
  DotApplied = "dot_applied",
  DotTick = "dot_tick",
  // === U3: damage-over-time === END
  // === U5: card mechanics polish === BEGIN (appended — never reorder)
  /**
   * Emitted when a card effect was attempted but failed a precondition.
   * Currently used by ChoreoUltimate when participating drones are scattered
   * (any drone further than CHOREO_FORMATION_RADIUS from the swarm centroid).
   */
  CardEffectFailed = "card_effect_failed",
  /**
   * Emitted when a player's card cooldown is reduced as a kill reward (the
   * cooldown-halve-on-kill polish). Carries the slot index plus the old/new
   * cooldown values so the HUD can animate the cooldown ring snapping back.
   */
  CardCooldownReduced = "card_cooldown_reduced",
  // === U5: card mechanics polish === END
}

/** Reason codes carried on `CardEffectFailed.data.reason`. */
export const CardFailedReason = {
  Scattered: 'scattered',
  ConvertShotRequiresSniper: 'convertshot_requires_sniper',
} as const;
export type CardFailedReason = typeof CardFailedReason[keyof typeof CardFailedReason];

// ── Game mode & phase ───────────────────────────────────────────────────────

export const GameMode = {
  Free: 'free',
  Br: 'br',
} as const;
export type GameMode = typeof GameMode[keyof typeof GameMode];

export const GamePhase = {
  Waiting: 'waiting',
  Playing: 'playing',
  Finished: 'finished',
} as const;
export type GamePhase = typeof GamePhase[keyof typeof GamePhase];

// ── Command interfaces ───────────────────────────────────────────────────────

export interface MoveCommand {
  type: CommandType.Move;
  direction: Vec3;
  speed: number;
}

export interface ActivateCardCommand {
  type: CommandType.ActivateCard;
  cardSlot: number;
}

export interface DiscardCardCommand {
  type: CommandType.DiscardCard;
  cardSlot: number;
}

export interface SetTargetCommand {
  type: CommandType.SetTarget;
  targetId: string;
  worldPos: Vec3;
}

export interface CancelOrderCommand {
  type: CommandType.CancelOrder;
}

export interface SelectDroneCommand {
  type: CommandType.SelectDrone;
  droneId: string;
  /**
   * true when the caller used Shift/Cmd-click to toggle the id in/out of the
   * current selection set. false/undefined = replace the selection with this id.
   */
  additive?: boolean;
}

export interface SetLeaderCommand {
  type: CommandType.SetLeader;
  droneId: string;
}

export interface TriggerAbilityCommand {
  type: CommandType.TriggerAbility;
  droneId: string;
  /** Camera/player-facing direction at the moment the ability was triggered. */
  direction?: Vec3;
}

export interface FireBasicCommand {
  type: CommandType.FireBasic;
  /** Camera/player-facing direction at the moment the volley was fired. */
  direction?: Vec3;
}

export interface DismissDroneCommand {
  type: CommandType.DismissDrone;
  droneId: string;
}

// === U7: admin debug commands === BEGIN
export interface AdminSpawnDummyCommand {
  type: CommandType.AdminSpawnDummy;
}

export interface AdminDamageSelfCommand {
  type: CommandType.AdminDamageSelf;
}

export interface AdminResetArenaCommand {
  type: CommandType.AdminResetArena;
}
// === U7: admin debug commands === END

export type PlayerCommand =
  | MoveCommand
  | ActivateCardCommand
  | DiscardCardCommand
  | SetTargetCommand
  | CancelOrderCommand
  | SelectDroneCommand
  | SetLeaderCommand
  | TriggerAbilityCommand
  | FireBasicCommand
  | DismissDroneCommand
  // === U7: admin debug commands === BEGIN
  | AdminSpawnDummyCommand
  | AdminDamageSelfCommand
  | AdminResetArenaCommand;
  // === U7: admin debug commands === END

// ── Server event payloads ────────────────────────────────────────────────────

export interface DroneDestroyedEvent {
  droneId: string;
  killerId: string;
  position: Vec3;
}

export interface DamageDealtEvent {
  targetDroneId: string;
  attackerDroneId: string;
  damage: number;
  /** True if this hit rolled a critical strike (damage already includes the multiplier). */
  isCrit: boolean;
  // === U5: knockback polish === BEGIN
  /**
   * Unit direction vector (length ~1) from the attacker toward the victim,
   * present only when the hit triggered a knockback impulse (finalDamage >=
   * KNOCKBACK_DAMAGE_THRESHOLD on a living target). Drives the radial dust
   * burst + local camera shake on the client. Absent for light hits.
   */
  knockback?: { x: number; y: number; z: number };
  // === U5: knockback polish === END
  // === U8: damage event enrichment === BEGIN
  /**
   * Unit direction vector (length ≈ 1) from attacker → victim. Always present
   * for basic combat hits resolved in `CombatResolver.resolveAttack`. Distinct
   * from `knockback` (which is only set for heavy-hit shoves) — `direction`
   * is unconditional and lets the client point a hit indicator regardless of
   * whether the shot was lethal or grazing.
   */
  direction?: { x: number; y: number; z: number };
  /**
   * Per-multiplier breakdown of the damage stack. Only keys whose value is
   * meaningfully ≠ 1 are included — the `1×` baseline is implicit. Populated
   * by `CombatResolver.resolveAttack` from the same multiplicative chain that
   * produced `damage`. Lets the client surface "CRIT! +50% Focus" or similar
   * floating text without re-deriving the math.
   */
  multipliers?: {
    crit?: number;
    supportAura?: number;
    focusFire?: number;
    syncDive?: number;
    defensiveWall?: number;
    falloff?: number;
  };
  // === U8: damage event enrichment === END
  // === U1: damage variance & accuracy roll === BEGIN
  /**
   * True when the attacker's accuracy roll failed. `damage` will be 0, no HP
   * change, no crit, no knockback. Lets the client render a "MISS" floater /
   * tracer-into-the-void effect without inferring it from the damage value.
   */
  missed?: boolean;
  // === U1: damage variance & accuracy roll === END
}

// === U8: assist tracking === BEGIN
/**
 * Emitted when a player's drone damaged a victim within the assist window
 * (10s) but did not deal the killing blow. The assister's owning player gets
 * +2 score; the event lets the client play an "ASSIST" toast distinct from a
 * full kill.
 */
export interface AssistGainedEvent {
  victimId: string;
  assisterPlayerId: string;
  killerId: string;
}
// === U8: assist tracking === END

export interface HealAppliedEvent {
  targetDroneId: string;
  healerDroneId: string;
  amount: number;
}

export interface PickupCollectedEvent {
  pickupId: string;
  playerId: string;
}

export interface AbilityTriggeredEvent {
  playerId: string;
  droneId: string;
  droneType: number;
  abilityName: string;
  position: Vec3;
}

export interface ProjectileFiredEvent {
  playerId: string;
  attackerDroneId: string;
  origin: Vec3;
  end: Vec3;
  targetDroneId?: string;
  hit: boolean;
}

// === U5: kill-streak announcer === BEGIN
/**
 * Multi-kill announcement: emitted when a player scores their 2nd/3rd/4th/5th
 * kill within a 5s rolling window (DOUBLE/TRIPLE/QUAD/PENTA respectively).
 * Only the most recent kill triggers the broadcast.
 */
export type KillStreakLevel = 'DOUBLE' | 'TRIPLE' | 'QUAD' | 'PENTA';

export interface KillStreakEvent {
  playerId: string;
  level: KillStreakLevel;
  killCount: number;
}
// === U5: kill-streak announcer === END

/**
 * Emitted when a Support's Shield Pulse buff absorbs an incoming hit and is
 * consumed. Drives a white expanding-ring flash + descending triad chime on
 * the client so the player can tell a free save from a normal damage tick.
 */
export interface ShieldBrokenEvent {
  droneId: string;
  position: Vec3;
}

// === Unit 4: ConvertShot glitch === BEGIN
/**
 * Emitted when ConvertShot (Sniper Legend-tier card) flips an enemy drone to
 * the attacker's team instead of destroying it. Carries both owner ids so the
 * client can render a kill-feed style notification ("A converted B's drone").
 */
export interface DroneConvertedEvent {
  convertedDroneId: string;
  position: Vec3;
  fromOwnerId: string;
  toOwnerId: string;
}
// === Unit 4: ConvertShot glitch === END

// ── Drone stats ──────────────────────────────────────────────────────────────

export interface DroneStats {
  tier: Tier;
  description: string;
  health: number;
  speed: number;
  damage: number;
  projectileRange: number;
  meleeRadius: number;
  cooldownMs: number;
  special?: string;
  // === U1: damage variance & accuracy roll === BEGIN
  /**
   * Probability in [0,1] that a basic combat hit lands. `accuracyRng() < accuracy`
   * is a hit; otherwise the attack fires its cooldown but emits a `missed`
   * DamageDealt event with `damage: 0` and applies no health change, knockback,
   * crit, or death check. Defaults to 1.0 (never miss) when omitted.
   */
  accuracy?: number;
  // === U1: damage variance & accuracy roll === END
  // === U2: damage falloff by range === BEGIN
  /**
   * Optional damage-by-distance falloff. When undefined (or `falloffStart`
   * <= 0), shots deal full base damage at every distance within range.
   *
   * Damage scales linearly from 1.0× at `<= falloffStart` down to
   * `falloffMin` at `>= falloffEnd`, then clamps. Lets long-range drones
   * (Sniper Rare/Legend) reward holding optimal stand-off distance without
   * making their max-range pokes oppressive.
   */
  falloffStart?: number;
  falloffEnd?: number;
  falloffMin?: number;
  // === U2: damage falloff by range === END
}

export const DRONE_STATS: Record<DroneType, DroneStats> = {
  [DroneType.Striker]: {
    tier: Tier.Common,
    description: "Frontline brawler. Fires short-range energy bolts and slashes through enemies in melee range. Fast cooldown makes it a relentless attacker.",
    health: 100, speed: 30, damage: 20, projectileRange: 6, meleeRadius: 2, cooldownMs: 800,
    accuracy: 0.85,
  },
  [DroneType.Sniper]: {
    tier: Tier.Rare,
    description: "Long-range precision unit. Slow but devastating single-target shots. Legend tier pierces through to a second target.",
    health: 80, speed: 30, damage: 20, projectileRange: 60, meleeRadius: 0, cooldownMs: 2500,
    special: "piercing",
    accuracy: 0.95,
    // === U2: damage falloff by range === BEGIN
    // Sniper damage tapers from full at <=30u to 0.5× at >=60u so close-range
    // pokes hit hardest and max-range shots feel chip-y. Applied to Rare and
    // Legend tiers in CombatResolver (Common Sniper is not a balanced loadout
    // option). Other drone types omit these fields entirely (= no falloff).
    falloffStart: 30,
    falloffEnd: 60,
    falloffMin: 0.5,
    // === U2: damage falloff by range === END
  },
  [DroneType.Healer]: {
    tier: Tier.Common,
    description: "Restores health to nearby allied drones. No attack damage. Legend tier can revive destroyed drones.",
    health: 80, speed: 30, damage: 0, projectileRange: 15, meleeRadius: 0, cooldownMs: 1000,
    special: "heal_15ps",
    accuracy: 1.0,
  },
  [DroneType.Interceptor]: {
    tier: Tier.Rare,
    description: "Ultra-fast flanker. Darts behind enemy lines to harass snipers and healers. Low health but hard to catch.",
    health: 90, speed: 30, damage: 20, projectileRange: 6, meleeRadius: 1.5, cooldownMs: 400,
    accuracy: 0.9,
  },
  [DroneType.Support]: {
    tier: Tier.Legend,
    description: "Aura buffer that boosts nearby allies' speed and damage. Weak on its own but force-multiplies the swarm.",
    health: 130, speed: 30, damage: 20, projectileRange: 18, meleeRadius: 0, cooldownMs: 800,
    special: "buff_speed_30_dmg_20_shield",
    accuracy: 0.9,
  },
  [DroneType.Guardian]: {
    tier: Tier.Rare,
    description: "As long as it's live, add max health allies by 30%",
    health: 100, speed: 30, damage: 20, projectileRange: 5, meleeRadius: 5, cooldownMs: 50000,
    special: "max_health_buff",
    accuracy: 0.9,
  },
};

// ── Active ability definitions ──────────────────────────────────────────────

export interface ActiveAbilityDef {
  name: string;
  description: string;
  cooldownMs: number;
  durationMs: number;
}

export const DRONE_ACTIVE_ABILITIES: Record<DroneType, ActiveAbilityDef> = {
  [DroneType.Striker]:     { name: 'Charge',       description: 'Dash 100m forward dealing AoE damage', cooldownMs: 8000, durationMs: 700 },
  [DroneType.Sniper]:      { name: 'Pierce Shot',  description: 'Guaranteed hit ignoring range',       cooldownMs: 12000, durationMs: 200 },
  [DroneType.Healer]:      { name: 'Burst Heal',   description: 'AoE heal pulse to nearby allies',     cooldownMs: 10000, durationMs: 300 },
  [DroneType.Interceptor]: { name: 'Afterburner',  description: '2x speed burst for 2 seconds',        cooldownMs: 6000,  durationMs: 2000 },
  [DroneType.Support]:     { name: 'Shield Pulse', description: 'Temporary shield to entire swarm',    cooldownMs: 15000, durationMs: 3000 },
  [DroneType.Guardian]:    { name: 'Taunt',        description: 'Draw all enemy fire for 4 seconds',   cooldownMs: 20000, durationMs: 4000 },
};

// ── Algorithm card definitions ───────────────────────────────────────────────

export interface AlgorithmCardDef {
  name: string;
  tier: Tier;
  cooldownSec: number;
  description: string;
  /**
   * Optional area-of-effect / influence radius in world units. Used by the
   * client UI to draw a hover range-indicator ring on the card bar.
   * Falls back to a sensible default (18) at the call site when omitted.
   */
  range?: number;
}

export const ALGORITHM_CARDS: Record<AlgorithmCardType, AlgorithmCardDef> = {
  [AlgorithmCardType.Regroup]: {
    name: "Regroup",
    tier: Tier.Common,
    cooldownSec: 5,
    description: "All drones converge to swarm center",
  },
  [AlgorithmCardType.Scatter]: {
    name: "Scatter",
    tier: Tier.Common,
    cooldownSec: 5,
    description: "Drones spread apart rapidly",
  },
  [AlgorithmCardType.Follow]: {
    name: "Follow",
    tier: Tier.Common,
    cooldownSec: 3,
    description: "Drones follow leader in line",
  },
  [AlgorithmCardType.Orbit]: {
    name: "Orbit",
    tier: Tier.Common,
    cooldownSec: 4,
    description: "Drones orbit around center point",
    range: ORBIT_ANIM_RADIUS,
  },
  [AlgorithmCardType.DefensiveWall]: {
    name: "Defensive Wall",
    tier: Tier.Rare,
    cooldownSec: 12,
    description: "Drones form flat wall shield",
    range: GUARDIAN_TAUNT_RADIUS,
  },
  [AlgorithmCardType.RadarRing]: {
    name: "Radar Ring",
    tier: Tier.Rare,
    cooldownSec: 12,
    description: "3D perimeter ring; enemies inside ring radius are revealed (markedAt) to teammates",
    range: RADAR_RING_RADIUS,
  },
  [AlgorithmCardType.FocusFire]: {
    name: "Focus Fire",
    tier: Tier.Rare,
    cooldownSec: 10,
    description: "Swarm focuses one enemy: +50% damage to the auto-target, +25% damage to others",
  },
  [AlgorithmCardType.RelayNet]: {
    name: "Relay Net",
    tier: Tier.Rare,
    cooldownSec: 10,
    description: "Connectivity mesh with range boost",
    range: SUPPORT_AURA_RANGE,
  },
  [AlgorithmCardType.CageSurround]: {
    name: "Cage Surround",
    tier: Tier.Legend,
    cooldownSec: 30,
    description: "Enclosing shell around target; the surrounded enemy lead drone is slowed by 60%",
    range: CAGE_SURROUND_RADIUS,
  },
  [AlgorithmCardType.SyncDive]: {
    name: "Sync Dive",
    tier: Tier.Legend,
    cooldownSec: 25,
    description: "Coordinated high-speed descent attack",
  },
  [AlgorithmCardType.ConvertShot]: {
    name: "Convert Shot",
    tier: Tier.Legend,
    cooldownSec: 40,
    description: "Sniper converts enemy drone to your side",
  },
  [AlgorithmCardType.ChoreoUltimate]: {
    name: "Choreo Ultimate",
    tier: Tier.Legend,
    cooldownSec: 45,
    description: "Cinematic formation damages nearby enemies",
    range: CHOREO_AOE_RADIUS,
  },
  [AlgorithmCardType.HorizontalOrbit]: {
    name: "Horizontal Orbit",
    tier: Tier.Common,
    cooldownSec: 4,
    description: "Selected drone orbits horizontally; swarm settles in idle ring",
    range: ORBIT_ANIM_RADIUS,
  },
  [AlgorithmCardType.DiagonalOrbit]: {
    name: "Diagonal Orbit",
    tier: Tier.Common,
    cooldownSec: 4,
    description: "Selected drone orbits along a tilted plane; swarm settles in idle ring",
    range: ORBIT_ANIM_RADIUS,
  },
};
